// frontend/src/pages/CreatorDirectory.jsx
// ✅ PROFESSIONAL DESIGN: No emojis, USD-based royalties

import React from "react";
import { Link } from "react-router-dom";
import Screen from "../uizard/ui/Screen"
import { getIdentityHeaders, waitForIdentity } from '../lib/identityGoogle';
import EarningsAnalytics from "../components/EarningsAnalytics.jsx";
import PaymentHistory from "../components/PaymentHistory.jsx";
import {
  API_BASE,
  toast
} from "../lib/api";
import { generateIdempotencyKey, checkIdempotencyKey, storeIdempotencyKey } from '../lib/idempotency';

const api = (path) => API_BASE ? `${API_BASE}${path}` : path;

const brand = {
  purple: "#6D5EF9",
  purpleDark: "#5B4EE8",
  purpleTint: "#F3F0FF",
  purpleBorder: "#E7E2FF",
  neutralText: "#111827",
  neutralBorder: "#E5E7EB",
  shadow: "0 10px 24px rgba(17,24,39,.08)",
};

const USD_PER_CREDIT = 0.17;

export default function CreatorDirectory() {
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);

  // Backend royalty data (USD)
  const [summaryLoading, setSummaryLoading] = React.useState(true);
  const [availableUSD, setAvailableUSD] = React.useState(0);
  const [pendingUSD, setPendingUSD] = React.useState(0);
  const [totalUSD, setTotalUSD] = React.useState(0);
  const [exchangedCredits, setExchangedCredits] = React.useState(0);
  const [stripeStatus, setStripeStatus] = React.useState(null);
  const [checkingStripe, setCheckingStripe] = React.useState(false);

  // ✅ NEW: Exchange amount input
  const [exchangeAmount, setExchangeAmount] = React.useState("");

  // Stats data
  const [hasAccess, setHasAccess] = React.useState(null);
  const [totalUses, setTotalUses] = React.useState(0);
  const [stylesCount, setStylesCount] = React.useState(0);

  // Collapsible sections
  const [earningsOpen, setEarningsOpen] = React.useState(() => {
    try { return localStorage.getItem("cd_earnings_open") !== "0"; } catch { return true; }
  });
  const [paymentsOpen, setPaymentsOpen] = React.useState(() => {
    try { return localStorage.getItem("cd_payments_open") === "1"; } catch { return false; }
  });
  const [statsOpen, setStatsOpen] = React.useState(() => {
    try { return localStorage.getItem("cd_stats_open") !== "0"; } catch { return true; }
  });

  // Fetch royalty summary
  const fetchRoyaltySummary = React.useCallback(async (forceRecalc = false) => {
    try {
      setSummaryLoading(true);
      
      const url = forceRecalc 
        ? api("/api/creator/royalties/summary?recalculate=true")
        : api("/api/creator/royalties/summary");
        
      const res = await fetch(url, {
        headers: getIdentityHeaders(),
      });
      
      if (res.ok) {
        const data = await res.json();
        setAvailableUSD(Number(data.availableUSD) || 0);
        setPendingUSD(Number(data.pendingUSD) || 0);
        setTotalUSD(Number(data.totalUSD) || 0);
        setExchangedCredits(Number(data.exchangedCredits) || 0);
      } else {
        console.warn("Failed to load royalty summary:", res.status);
      }
    } catch (e) {
      console.warn("Failed to load royalty summary:", e);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  // Fetch stats
  const fetchStats = React.useCallback(async () => {
    try {
      setLoading(true);
      await waitForIdentity();

      const res = await fetch(api("/api/creator/royalties/by-style?period=all"), {
        headers: getIdentityHeaders(),
      });

      if (res.ok) {
        const data = await res.json();
        const items = Array.isArray(data.items) ? data.items : [];
        
        const uses = items.reduce((sum, item) => sum + (item.uses || 0), 0);
        setTotalUses(uses);
        setStylesCount(items.length);
      }
    } catch (e) {
      console.error("Failed to load stats:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial data load
  React.useEffect(() => {
    fetchRoyaltySummary();
    fetchStats();
  }, [fetchRoyaltySummary, fetchStats]);

  // Refresh function
  const refreshAllData = React.useCallback(async () => {
    setRefreshing(true);
    
    try {
      await Promise.all([
        fetchRoyaltySummary(true),
        fetchStats(),
      ]);
      
      toast("Data refreshed successfully");
    } catch (e) {
      console.error("Refresh failed:", e);
      toast("Refresh failed: " + (e.message || "Unknown error"));
    } finally {
      setRefreshing(false);
    }
  }, [fetchRoyaltySummary, fetchStats]);

  React.useEffect(() => {
  async function checkAccess() {
    try {
      const res = await fetch(api('/api/creator/styles'), {
        headers: getIdentityHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setHasAccess(data.items?.length > 0);
      } else {
        setHasAccess(false);
      }
    } catch (e) {
      console.error("Failed to check access:", e);
      setHasAccess(false);
    }
  }
  checkAccess();
}, []);

const creditsAvailable = Math.floor(availableUSD / USD_PER_CREDIT);

  function toggleEarnings() {
    const next = !earningsOpen;
    setEarningsOpen(next);
    try { localStorage.setItem("cd_earnings_open", next ? "1" : "0"); } catch {}
  }

  function togglePayments() {
    const next = !paymentsOpen;
    setPaymentsOpen(next);
    try { localStorage.setItem("cd_payments_open", next ? "1" : "0"); } catch {}
  }

  function toggleStats() {
    const next = !statsOpen;
    setStatsOpen(next);
    try { localStorage.setItem("cd_stats_open", next ? "1" : "0"); } catch {}
  }

  // ✅ Helper to get Google ID
  async function getGoogleId() {
    try {
      await waitForIdentity();
      const identity = window.googleIdentity;
      return identity?.sub || 'unknown';
    } catch (e) {
      console.error("Failed to get Google ID:", e);
      return 'unknown';
    }
  }

  // ✅ UPDATED: Exchange with custom amount
  async function exchangeForCredits() {
    const amountUSD = exchangeAmount 
      ? Math.round(parseFloat(exchangeAmount) * 100) / 100 
      : Math.round(availableUSD * 100) / 100;
      
    if (!(amountUSD > 0)) {
      toast("Please enter a valid amount");
      return;
    }
    
    if (amountUSD > availableUSD) {
      toast(`Amount exceeds available balance ($${availableUSD.toFixed(2)})`);
      return;
    }

    const creditsToReceive = Math.floor(amountUSD / USD_PER_CREDIT);
    
    if (creditsToReceive < 1) {
      toast("Minimum exchange: $0.17 for 1 credit");
      return;
    }
    
    if (!window.confirm(
      `Exchange $${amountUSD.toFixed(2)} for ${creditsToReceive} credits?`
    )) return;

    try {
      setRefreshing(true);

      const googleId = await getGoogleId();
      const idempotencyKey = generateIdempotencyKey(googleId, 'exchange');
      
      const cached = checkIdempotencyKey(idempotencyKey);
      if (cached) {
        toast("Exchange already processed");
        window.dispatchEvent(new Event('credits:refresh'));
        await fetchRoyaltySummary();
        setExchangeAmount(""); // ✅ Clear input
        return;
      }

      const res = await fetch(api("/api/creator/royalties/exchange"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getIdentityHeaders(),
        },
        body: JSON.stringify({
          amountUSD,
          idempotencyKey,
        }),
      });

      const data = await res.json();
      
      if (res.ok) {
        storeIdempotencyKey(idempotencyKey, { amountUSD, creditsToReceive });
        
        toast(`Exchanged $${amountUSD.toFixed(2)} for ${creditsToReceive} credits successfully`);
        
        window.dispatchEvent(new Event('credits:refresh'));
        await fetchRoyaltySummary();
        setExchangeAmount(""); // ✅ Clear input
        
      } else if (res.status === 429) {
        toast(`Too many requests. Please wait ${data.retryAfter || 15} minutes.`);
      } else {
        toast(`Exchange failed: ${data?.error || "Unknown error"}`);
        console.error("Exchange failed:", data);
      }
    } catch (e) {
      toast(`Exchange error: ${e.message || "Network error"}`);
      console.error("Exchange exception:", e);
    } finally {
      setRefreshing(false);
    }
  }

  // Check Stripe connection status
  const checkStripeStatus = React.useCallback(async () => {
    try {
      setCheckingStripe(true);
      await waitForIdentity();
      
      const res = await fetch(api('/api/creator/stripe/status'), {
        headers: getIdentityHeaders(),
      });
      
      if (res.ok) {
        const data = await res.json();
        setStripeStatus(data);
      }
    } catch (e) {
      console.error("Failed to check Stripe status:", e);
    } finally {
      setCheckingStripe(false);
    }
  }, []);

  React.useEffect(() => {
    checkStripeStatus();
  }, [checkStripeStatus]);

  // Connect Stripe account
  async function connectStripe() {
    try {
      await waitForIdentity();
      
      const res = await fetch(api('/api/creator/stripe/connect'), {
        method: 'POST',
        headers: getIdentityHeaders(),
      });
      
      if (res.ok) {
        const data = await res.json();
        
        if (data.autoConnected) {
          toast("Platform owner - auto-connected!");
          await checkStripeStatus();
        } else if (data.url) {
          window.location.href = data.url;
        }
      } else {
        toast("Failed to connect Stripe");
      }
    } catch (e) {
      console.error("Stripe connect error:", e);
      toast("Failed to connect Stripe");
    }
  }

  // Withdraw USD
  async function withdrawUSD() {
    const amountUSD = Math.round(availableUSD * 100) / 100;
    
    if (amountUSD < 1) {
      toast("Minimum withdrawal is $1");
      return;
    }
    
    if (!window.confirm(`Withdraw $${amountUSD.toFixed(2)} to your Stripe account?`)) {
      return;
    }
    
    try {
      setRefreshing(true);
      
      const googleId = await getGoogleId();
      const idempotencyKey = generateIdempotencyKey(googleId, 'withdraw');
      
      const cached = checkIdempotencyKey(idempotencyKey);
      if (cached) {
        toast("Withdrawal already processed");
        await fetchRoyaltySummary();
        return;
      }
      
      const res = await fetch(api('/api/creator/royalties/withdraw'), {
        method: 'POST',
        headers: {
          ...getIdentityHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          amountUSD,
          idempotencyKey,
        }),
      });
      
      const data = await res.json();
      
      if (res.ok) {
        storeIdempotencyKey(idempotencyKey, { amountUSD });
        
        toast(`Withdrawal successful! $${amountUSD.toFixed(2)}`);
        await fetchRoyaltySummary();
      } else if (res.status === 429) {
        toast(`Too many requests. Please wait ${data.retryAfter || 15} minutes.`);
      } else {
        toast(`${data.error || 'Withdrawal failed'}`);
      }
    } catch (e) {
      console.error("Withdrawal error:", e);
      toast("Withdrawal failed");
    } finally {
      setRefreshing(false);
    }
  }

  // ✅ ACCESS CONTROL - Check if user owns styles
  if (hasAccess === null) {
    return (
      <Screen>
        <div style={{padding: '40px', textAlign: 'center'}}>
          <div style={{fontSize: '18px', color: '#6B7280'}}>Loading...</div>
        </div>
      </Screen>
    );
  }

  if (hasAccess === false) {
    return (
      <Screen>
        <StyleTag />
        <div style={{
          padding: '40px',
          textAlign: 'center',
          maxWidth: '600px',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontSize: '28px',
            fontWeight: '900',
            marginBottom: '16px',
            background: 'linear-gradient(135deg, #1E3A8A 0%, #6D28D9 100%)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            Style Owners Only
          </h2>
          <p style={{color: '#6B7280', marginTop: '16px', fontSize: '16px', lineHeight: '1.6'}}>
            This section is only available to creators who own custom styles on ArtiBytes.
          </p>
          <Link to="/profile" style={{
            display: 'inline-block',
            marginTop: '24px',
            padding: '12px 24px',
            background: '#6D5EF9',
            color: 'white',
            borderRadius: '12px',
            textDecoration: 'none',
            fontWeight: '700',
            transition: 'all 0.2s'
          }}>
            ← Back to Profile
          </Link>
        </div>
      </Screen>
    );
  }

  // Calculate credits for current input
  const inputCredits = Math.floor((parseFloat(exchangeAmount) || 0) / USD_PER_CREDIT);

  // ✅ If hasAccess === true, continue with normal render
  return (
    <Screen>
      <StyleTag />

      {/* Hero */}
      <section className="cd-hero">
        <h1 
          className="h1"
          style={{
            background: "linear-gradient(135deg, #1E3A8A 0%, #6D28D9 100%)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Creator Economy
        </h1>
        <div className="ms-hero__subtitle-wrapper">
          <p className="ms-hero__subtitle">
            Track earnings • Exchange for credits • Manage payouts
          </p>
          <p className="ms-hero__tips">
            Toggle sections for detailed breakdowns or refresh to update data
          </p>
        </div>
      </section>

      {/* Nav */}
      <section className="cd-local-nav">
        <Link to="/profile" className="btn">Back to Profile</Link>
        <button 
          className="btn" 
          onClick={refreshAllData} 
          disabled={refreshing}
          title="Refresh all data"
        >
          {refreshing ? "Refreshing..." : "Refresh Data"}
        </button>
        <button className="btn" onClick={toggleStats}>
          {statsOpen ? "Hide" : "Show"} Activity
        </button>
        <button className="btn" onClick={toggleEarnings}>
          {earningsOpen ? "Hide" : "Show"} Analytics
        </button>
        <button className="btn" onClick={togglePayments}>
          {paymentsOpen ? "Hide" : "Show"} History
        </button>
      </section>

      {/* KPIs */}
      {statsOpen && (
        <section className="cd-kpis">
          <Kpi label="Total uses" value={loading ? "—" : totalUses.toLocaleString()} />
          <Kpi 
            label="All-time royalties" 
            value={summaryLoading ? "—" : `$${totalUSD.toFixed(2)}`}
          />
        </section>
      )}

      {/* Royalty Breakdown */}
      <section className="cd-royalty-breakdown">
        <div className="cd-card">
          {/* Exchange Rate */}
          <div className="live-rate-section">
            <div className="live-rate-badge">
              <span className="live-rate-label">Royalty Ledger</span>
            </div>
          </div>

          <div className="cd-breakdown-grid">
            <div className="breakdown-item">
              <div className="breakdown-label">Available</div>
              <div className="breakdown-value">
                {summaryLoading ? "—" : `$${availableUSD.toFixed(2)}`}
              </div>
            </div>
            <div className="breakdown-item">
              <div className="breakdown-label">Credits</div>
              <div className="breakdown-value highlight">
                {summaryLoading ? "—" : creditsAvailable.toLocaleString()}
              </div>
              <div className="breakdown-hint">
                {availableUSD > 0
                  ? `$${availableUSD.toFixed(2)} = ${creditsAvailable} credits` 
                  : `Rate: $${USD_PER_CREDIT} per credit`}
              </div>
            </div>
            <div className="breakdown-item">
              <div className="breakdown-label">Total earned</div>
              <div className="breakdown-value muted">
                {summaryLoading ? "—" : `$${totalUSD.toFixed(2)}`}
              </div>
            </div>
          </div>
          
          {/* ✅ NEW: Exchange Input Section */}
          <div className="exchange-section">
            <div className="input-group">
              <label htmlFor="exchange-amount" className="input-label">
                Exchange Amount (USD)
              </label>
              <input
                id="exchange-amount"
                type="number"
                min="0"
                max={availableUSD}
                step="0.01"
                value={exchangeAmount}
                onChange={(e) => setExchangeAmount(e.target.value)}
                placeholder={`Max: $${availableUSD.toFixed(2)}`}
                className="exchange-input"
                disabled={summaryLoading || refreshing}
              />
              <div className="input-hint">
                = {inputCredits} {inputCredits === 1 ? 'credit' : 'credits'}
              </div>
            </div>

            {/* Quick amount buttons */}
            <div className="quick-amounts">
              <button 
                onClick={() => setExchangeAmount("1.00")} 
                className="quick-btn"
                disabled={availableUSD < 1 || summaryLoading || refreshing}
              >
                $1 <span className="quick-credits">(5c)</span>
              </button>
              <button 
                onClick={() => setExchangeAmount("5.00")} 
                className="quick-btn"
                disabled={availableUSD < 5 || summaryLoading || refreshing}
              >
                $5 <span className="quick-credits">(29c)</span>
              </button>
              <button 
                onClick={() => setExchangeAmount("10.00")} 
                className="quick-btn"
                disabled={availableUSD < 10 || summaryLoading || refreshing}
              >
                $10 <span className="quick-credits">(58c)</span>
              </button>
              <button 
                onClick={() => setExchangeAmount(availableUSD.toFixed(2))} 
                className="quick-btn quick-btn-all"
                disabled={availableUSD < 0.17 || summaryLoading || refreshing}
              >
                All <span className="quick-credits">({creditsAvailable}c)</span>
              </button>
            </div>

            {/* Action Buttons */}
            <div className="action-buttons">
              {stripeStatus?.connected && stripeStatus?.verified ? (
                <button
                  className="btn btn-secondary action-btn"
                  disabled={summaryLoading || availableUSD < 1 || refreshing}
                  onClick={withdrawUSD}
                  title={availableUSD >= 1 ? "Withdraw to Stripe" : "Minimum $1"}
                >
                  Withdraw USD
                </button>
              ) : stripeStatus?.isPlatformOwner ? (
                <button
                  className="btn btn-secondary action-btn"
                  disabled={summaryLoading || availableUSD < 1 || refreshing}
                  onClick={withdrawUSD}
                  title="Platform owner withdrawal"
                >
                  Withdraw USD
                </button>
              ) : (
                <button
                  className="btn btn-secondary action-btn"
                  onClick={connectStripe}
                  disabled={checkingStripe}
                  title="Connect Stripe to enable withdrawals"
                >
                  {checkingStripe ? "Checking..." : "Connect Stripe"}
                </button>
              )}
              
              <button
                className="btn btn-primary action-btn"
                disabled={
                  summaryLoading || 
                  refreshing || 
                  !exchangeAmount || 
                  parseFloat(exchangeAmount) <= 0 ||
                  parseFloat(exchangeAmount) > availableUSD ||
                  inputCredits < 1
                }
                onClick={exchangeForCredits}
                title={
                  !exchangeAmount 
                    ? "Enter an amount to exchange"
                    : parseFloat(exchangeAmount) > availableUSD 
                    ? "Amount exceeds available balance"
                    : inputCredits < 1
                    ? "Minimum exchange: $0.17 (1 credit)"
                    : `Exchange for ${inputCredits} credits`
                }
              >
                {exchangeAmount && inputCredits >= 1
                  ? `Exchange for ${inputCredits} ${inputCredits === 1 ? 'Credit' : 'Credits'}`
                  : 'Exchange for Credits'
                }
              </button>
            </div>
          </div>
          
          <div className="withdraw-note">
            Withdraw USD to your payment method, or exchange for credits to use on ArtiBytes.
            Fixed rate: 1 credit = ${USD_PER_CREDIT}
          </div>
        </div>
      </section>

      {/* Earnings Analytics */}
      {earningsOpen && (
        <EarningsAnalytics />
      )}

      {/* Payment History */}
      {paymentsOpen && (
        <section className="cd-analytics-section">
          <div className="cd-card">
            <PaymentHistory />
          </div>
        </section>
      )}

    </Screen>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="kpi">
      <div className="kpi__value">{value}</div>
      <div className="kpi__label">{label}</div>
    </div>
  );
}

function StyleTag() {
  return (
    <style>{`
.cd-hero{
  padding:18px; border:1px solid ${brand.purpleBorder}; border-radius:16px;
  background: linear-gradient(180deg, ${brand.purpleTint}, #fff);
  margin-bottom:18px;
}

.ms-hero__subtitle-wrapper {
  margin-top: 12px;
}

.ms-hero__subtitle{ 
  color: #6B7280; 
  font-size: 15px;
  margin: 0 0 8px 0;
  font-weight: 600;
}

.ms-hero__tips {
  color: #9CA3AF;
  font-size: 13px;
  margin: 0;
  line-height: 1.5;
}

@media (max-width: 640px) {
  .ms-hero__subtitle {
    font-size: 14px;
  }
  
  .ms-hero__tips {
    font-size: 12px;
  }
}

.cd-local-nav{ display:flex; gap:10px; margin-bottom:18px; flex-wrap:wrap; }

@media (max-width: 640px){
  .cd-local-nav{
    gap:6px;
  }
  
  .cd-local-nav .btn{
    font-size:13px;
    height:38px;
    padding:0 12px;
  }
}

.cd-kpis{
  display:grid; grid-template-columns:repeat(2,1fr); gap:12px; margin-bottom:18px;
}

@media (max-width: 600px){ 
  .cd-kpis{ 
    gap:8px;
  }
  .kpi{
    padding:10px 8px;
  }
  .kpi__value{ 
    font-size:20px;
  }
  .kpi__label{ 
    font-size:11px;
  }
}

.kpi{
  background:#fff; border:1px solid ${brand.neutralBorder}; border-radius:14px;
  padding:14px; text-align:center; box-shadow:${brand.shadow};
}
.kpi__value{ font-size:26px; font-weight:800; color:${brand.neutralText}; }
.kpi__label{ color:${brand.neutralText}; opacity:.7; font-size:14px; margin-top:4px; }

.cd-royalty-breakdown{ margin-bottom:18px; }

.live-rate-section{
  background: linear-gradient(135deg, ${brand.purpleTint} 0%, #FAFAFF 100%);
  border: 1px solid ${brand.purpleBorder};
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
}

.live-rate-badge{
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  justify-content: space-between;
}

.live-rate-label{
  font-size: 14px;
  font-weight: 700;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.live-rate-value{
  font-size: 16px;
  font-weight: 900;
  color: ${brand.neutralText};
  background: white;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid ${brand.purpleBorder};
}

@media (max-width: 600px) {
  .live-rate-section{
    padding: 10px 12px;
  }
  
  .live-rate-label{
    font-size: 12px;
  }
  
  .live-rate-value{
    font-size: 14px;
    padding: 4px 10px;
  }
}

.cd-breakdown-grid{
  display:grid; 
  grid-template-columns:repeat(3,1fr); 
  gap:12px; 
  margin-bottom:16px;
}

@media (max-width: 900px){ 
  .cd-breakdown-grid{ 
    gap:8px;
  }
  
  .breakdown-item{
    padding: 12px 8px;
    background: #FAFAFA;
    border-radius: 10px;
    border: 1px solid ${brand.purpleBorder};
  }
  
  .breakdown-label{ 
    font-size:11px;
    margin-bottom:4px;
  }
  
  .breakdown-value{ 
    font-size:18px;
  }
  
  .breakdown-hint{
    font-size:10px;
    margin-top:2px;
  }
}

.breakdown-item{ text-align:center; }
.breakdown-label{ color:#6B7280; font-size:14px; margin-bottom:6px; font-weight:700; }
.breakdown-value{ font-size:22px; font-weight:900; color:${brand.neutralText}; }
.breakdown-value.muted{ opacity:.85; }
.breakdown-value.highlight{ color:${brand.purple}; }
.breakdown-hint{
  font-size:12px;
  color:#9CA3AF;
  margin-top:4px;
  font-weight:600;
}

/* ✅ NEW: Exchange section styles */
.exchange-section {
  margin-bottom: 16px;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
}

.input-label {
  font-size: 13px;
  font-weight: 700;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.exchange-input {
  height: 48px;
  padding: 0 16px;
  border: 2px solid ${brand.purpleBorder};
  border-radius: 12px;
  font-size: 18px;
  font-weight: 700;
  color: ${brand.neutralText};
  background: white;
  transition: all 0.2s;
  font-family: inherit;
}

.exchange-input:focus {
  outline: none;
  border-color: ${brand.purple};
  box-shadow: 0 0 0 3px ${brand.purpleTint};
}

.exchange-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #F9FAFB;
}

.exchange-input::placeholder {
  color: #9CA3AF;
  font-weight: 600;
}

.input-hint {
  font-size: 14px;
  font-weight: 600;
  color: ${brand.purple};
  padding: 6px 12px;
  background: ${brand.purpleTint};
  border-radius: 8px;
  text-align: center;
}

.quick-amounts {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 12px;
}

.quick-btn {
  height: 40px;
  padding: 0 8px;
  border: 1.5px solid ${brand.purpleBorder};
  border-radius: 8px;
  background: white;
  color: ${brand.purple};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
}

.quick-btn:hover:not(:disabled) {
  background: ${brand.purpleTint};
  border-color: ${brand.purple};
  transform: translateY(-1px);
}

.quick-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.quick-btn-all {
  background: ${brand.purpleTint};
  border-color: ${brand.purple};
  font-weight: 800;
}

.quick-credits {
  font-size: 11px;
  font-weight: 600;
  opacity: 0.8;
}

@media (max-width: 640px) {
  .exchange-input {
    height: 44px;
    font-size: 16px;
  }
  
  .input-label {
    font-size: 11px;
  }
  
  .input-hint {
    font-size: 12px;
    padding: 4px 10px;
  }
  
  .quick-amounts {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .quick-btn {
    height: 36px;
    font-size: 13px;
  }
  
  .quick-credits {
    font-size: 10px;
  }
}

.action-buttons{
  display:grid;
  grid-template-columns:repeat(2, 1fr);
  gap:12px;
  margin-bottom:12px;
}

@media (max-width: 640px){
  .action-buttons{
    grid-template-columns:1fr;
    gap:8px;
  }
  
  .action-btn{
    height:44px;
    font-size:14px;
  }
}

.action-btn{
  height:48px;
  font-size:15px;
  font-weight:800;
}

.withdraw-note{ 
  text-align:center; 
  color:#6B7280; 
  font-size:13px; 
  line-height:1.5;
}

@media (max-width: 640px){
  .withdraw-note{
    font-size:11px;
  }
}

.cd-analytics-section{ margin-bottom:18px; }

.cd-card{
  background:#fff; border:1px solid ${brand.neutralBorder}; border-radius:16px;
  padding:18px; box-shadow:${brand.shadow};
}

.btn{
  height:42px; padding:0 16px; border-radius:12px;
  border:1px solid ${brand.purpleBorder}; 
  background: ${brand.purpleTint};
  color: ${brand.neutralText};
  font-weight:700; cursor:pointer; transition: all 0.2s;
  text-decoration:none; display:inline-flex; align-items:center; justify-content:center;
}
.btn:hover{
  background: #E9DEFF;
  border-color: ${brand.purple};
}
.btn-primary{ background:${brand.purple}; color:#fff; border-color:${brand.purple}; }
.btn-primary:hover{ background:${brand.purpleDark}; }
.btn-secondary{
  background:#fff;
  color:${brand.purple};
  border:1.5px solid ${brand.purpleBorder};
  font-weight:800;
}
.btn-secondary:hover{
  background:${brand.purpleTint};
  border-color:${brand.purple};
}
.btn:disabled{ opacity:.6; cursor:not-allowed; }

.h1{ font-size:32px; font-weight:900; margin:0; }
    `}</style>
  );
}